import { supabase } from "./supabase";

/**
 * O IO da administração do Radar Simples (000190).
 *
 * Zero regra: quem decide se pode é `pode_editar('radar')` (000297) DENTRO de cada RPC, e quem
 * valida a coerência (anexo × permissão, repartição somando 100%) é o banco.
 * Este arquivo lê, grava e tipa.
 *
 * Nenhum `select` cru: `cnaes` tem `revoke all` desde que era `rs_cnaes`, e as
 * três tabelas de números também. A leitura sai por função — a lição da 000136.
 */

export type CnaeAdmin = {
  codigo: string;
  num_cnae: string;
  descricao: string;
  tipo: string;
  permitido_simples: "Sim" | "Não" | "Revisar";
  anexo_principal: number | null;
  exige_fator_r: boolean;
  classificacao: string | null;
  ressalva: string | null;
  nota_fpas_rat: string | null;
  base_legal: string | null;
  fpas: string | null;
  rat: number | null;
  grau_risco: number | null;
  fpas_rat_criterio: string | null;
  ativo: boolean;
  nota_de_origem: string | null;
  conferido_em: string | null;
  conferido_por: string | null;
  /** Quantas ocupações do MEI apontam para este código — a lista é UMA. */
  ocupacoes_mei: number;
};

export type ResumoDoCatalogo = {
  total: number;
  a_revisar: number;
  com_nota: number;
  conferidos: number;
  desligados: number;
};

export type PaginaDeCnaes = {
  ok: true;
  termo: string;
  situacao: string;
  teto: number;
  offset: number;
  total: number;
  resumo: ResumoDoCatalogo;
  cnaes: CnaeAdmin[];
};

export type ParametroAdmin = {
  chave: string;
  valor: number;
  rotulo: string;
  referencia: string;
  unidade: string;
  atualizado_em: string;
};

export type FaixaAdmin = {
  anexo: number;
  faixa: number;
  receita_min: number;
  receita_max: number;
  aliquota_nominal: number;
  parcela_deduzir: number;
  irpj: number;
  csll: number;
  cofins: number;
  pis: number;
  cpp: number;
  ipi: number;
  icms: number;
  iss: number;
  /** A soma da repartição, calculada no BANCO. É ela que denuncia faixa torta. */
  soma: number;
};

export type Resposta = { ok: boolean; motivo?: string; mensagem?: string; soma?: number };

/** A frase de cada recusa. O banco manda a mensagem; isto é a rede de segurança. */
export function mensagemDoAdmin(r: Resposta | null, erro?: string): string {
  if (erro) return erro;
  if (!r) return "não foi possível falar com o servidor";
  if (r.mensagem) return r.mensagem;
  switch (r.motivo) {
    case "inexistente":
      return "este item não existe mais";
    case "anexo_faltando":
      return "um CNAE permitido no Simples precisa de anexo";
    case "anexo_sobrando":
      return "um CNAE vedado ao Simples não tem anexo";
    case "reparticao_nao_fecha":
      return "a repartição precisa somar 100%";
    default:
      return "não foi possível salvar";
  }
}

export async function lerCnaes(opcoes: {
  termo?: string;
  situacao?: string;
  limite?: number;
  offset?: number;
}): Promise<PaginaDeCnaes> {
  const { data, error } = await supabase.rpc("rs_admin_cnaes", {
    p_termo: opcoes.termo ?? "",
    p_situacao: opcoes.situacao ?? "todos",
    p_limite: opcoes.limite ?? 50,
    p_offset: opcoes.offset ?? 0,
  });
  if (error) throw error;
  return data as PaginaDeCnaes;
}

export async function salvarCnae(campos: {
  codigo: string;
  descricao?: string;
  tipo?: string;
  permitido_simples?: string;
  anexo_principal?: number | null;
  exige_fator_r?: boolean;
  classificacao?: string;
  ressalva?: string;
  base_legal?: string;
  nota_de_origem?: string;
  ativo?: boolean;
  confirmar?: boolean;
}): Promise<Resposta> {
  const { data, error } = await supabase.rpc("rs_admin_salvar_cnae", {
    p_codigo: campos.codigo,
    p_descricao: campos.descricao ?? null,
    p_tipo: campos.tipo ?? null,
    p_permitido_simples: campos.permitido_simples ?? null,
    p_anexo_principal: campos.anexo_principal ?? null,
    p_exige_fator_r: campos.exige_fator_r ?? null,
    p_classificacao: campos.classificacao ?? null,
    // Ressalva e nota são APAGÁVEIS: string vazia limpa, `null` não mexe. O
    // trabalho da equipe costuma ser justamente tirar o aviso.
    p_ressalva: campos.ressalva ?? null,
    p_base_legal: campos.base_legal ?? null,
    p_nota_de_origem: campos.nota_de_origem ?? null,
    p_ativo: campos.ativo ?? null,
    p_confirmar: campos.confirmar ?? false,
  });
  if (error) return { ok: false, mensagem: error.message };
  return data as Resposta;
}

export async function lerNumeros(): Promise<{
  ok: true;
  parametros: ParametroAdmin[];
  faixas: FaixaAdmin[];
}> {
  const { data, error } = await supabase.rpc("rs_admin_numeros");
  if (error) throw error;
  return data as { ok: true; parametros: ParametroAdmin[]; faixas: FaixaAdmin[] };
}

export async function salvarParametro(
  chave: string,
  valor: number,
  referencia?: string,
): Promise<Resposta> {
  const { data, error } = await supabase.rpc("rs_admin_salvar_parametro", {
    p_chave: chave,
    p_valor: valor,
    p_referencia: referencia ?? null,
  });
  if (error) return { ok: false, mensagem: error.message };
  return data as Resposta;
}

export async function salvarFaixa(f: FaixaAdmin): Promise<Resposta> {
  const { data, error } = await supabase.rpc("rs_admin_salvar_faixa", {
    p_anexo: f.anexo,
    p_faixa: f.faixa,
    p_aliquota_nominal: f.aliquota_nominal,
    p_parcela_deduzir: f.parcela_deduzir,
    p_irpj: f.irpj,
    p_csll: f.csll,
    p_cofins: f.cofins,
    p_pis: f.pis,
    p_cpp: f.cpp,
    p_ipi: f.ipi,
    p_icms: f.icms,
    p_iss: f.iss,
  });
  if (error) return { ok: false, mensagem: error.message };
  return data as Resposta;
}
